import { useGet } from "@albertine/api-client";
import { MemberMarkers } from "@prototypes/member-markers/MemberMarkers";
import classNames from "classnames";
import { NavLink, useHistory } from "react-router-dom";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useMembersMap } from "../../common/hooks/useMembers";
import type { Agent } from "../../common/types/Agent";
import { DeadlineStepToTextMap } from "../../common/types/Deadline";
import { categoryDisplayName } from "../../common/types/OfferingCategory";
import { defaultRequestTitle, type Request } from "../../common/types/Request";
import { RequestStatusMap } from "../../common/types/RequestStatus";
import {
    requestIsActionable as isRequestActionable,
    requestNoFurtherAction,
    requestOutcome,
} from "../../common/utils/requests.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { formatRequestDateTimeStrings } from "../../utils/time.util";
import { AgentAvatarWithSecondary } from "../Common/Avatar";
import Button from "../Common/Buttons";
import DeadlineTag from "../Common/DeadlineTag";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import { List } from "../Common/List";
import Stars from "../Common/rating/Stars";
import Tag from "../Common/Tag";
import { TicketNumberSmall } from "../Common/TicketNumber";
import TimeTag from "../Common/TimeTag";
import Tooltip from "../Common/Tooltip";
import AssignToMeButton from "./AssignToMeButton";
import "./RequestCard.css";
import { OfferingCategory } from "@albertine/types";
import type { MembershipTier } from "@albertine/types/openapi/lmt";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { CategoryIconMap, PictogramIcon } from "../Common/Icon";
import Label from "../Common/Label";

type Props = {
    currentAgent?: Agent;
    request: Request | undefined;
    layout: "horizontal" | "vertical";
    showEditButton?: boolean;
    showMemberName?: boolean;
    showStatusTag?: boolean;
};

const RequestCard = function RequestCard({
    currentAgent,
    request,
    layout,
    showEditButton = false,
    showMemberName = true,
    showStatusTag = false,
}: Props) {
    const history = useHistory();
    const agentsMap = useAgentsMap();
    const membersMap = useMembersMap();
    const { data: membershipTiers = [] } = useGet("membershipTiers", {});

    if (!request) return null;

    const url = `/request/${request?.id}`;

    const agent = request.assignedTo && agentsMap[request.assignedTo];
    const member = membersMap[request.member];
    const membershipTierName = membershipTiers.find(
        (tier: MembershipTier) => tier.id === member?.membership?.tier,
    )?.name;
    const isAssignedToMemberOwner =
        request.assignedTo !== undefined &&
        request.assignedTo === member?.assignedToAgentId;
    const agentTheMemberIsAssignedTo = member?.assignedToAgentId
        ? agentsMap[member.assignedToAgentId]
        : undefined;

    const requestDateFormatted = formatRequestDateTimeStrings(
        request.startDate,
        request.startTime,
        request.endDate,
    );
    const requestDateFormattedWithDayPrefix = formatRequestDateTimeStrings(
        request.startDate,
        request.startTime,
        request.endDate,
        new Date(),
        true,
    );
    const CategoryIconComponent =
        PictogramIcon[CategoryIconMap[request.offeringCategory || "other"]];

    const categoryIcon = (
        <div
            className="request-card__request-category web-lib__tooltip web-lib__tooltip--right"
            data-tooltip={
                request.offeringCategory
                    ? categoryDisplayName(request.offeringCategory, "en")
                    : categoryDisplayName("uncategorized", "en")
            }
        >
            <CategoryIconComponent.Medium.N90 />
        </div>
    );

    const ticketNumber = (
        <span className="request-card__ticket-number">
            <TicketNumberSmall
                ticketNumber={request.ticketNumber}
                className={
                    layout === "vertical" ? "ticket-number--backgroundless" : undefined
                }
            />
        </span>
    );

    const requestTitle = (
        <div
            className={classNames("request-card__title", {
                "request-card__title--missing": !request.title,
            })}
        >
            {request.title || defaultRequestTitle}
        </div>
    );

    const requestCategory =
        request.offeringCategory &&
        isTypeOf(request.offeringCategory, OfferingCategory) ? (
            <span>{categoryDisplayName(request.offeringCategory, "en")}</span>
        ) : null;

    const mobileEditButton = (
        <Button
            label="Edit"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const mobileEditLink = `/request/${request?.id}/edit`;
                history.push(mobileEditLink);
            }}
        />
    );

    const requestDateRange = (
        <span>
            {requestDateFormatted ? (
                <Tooltip label={requestDateFormattedWithDayPrefix || ""}>
                    {requestDateFormatted}
                </Tooltip>
            ) : (
                "No date set"
            )}
        </span>
    );

    const memberName = (
        <span className="inline-flex items-center gap-1">
            {membersMap[request.member]?.firstName || ""}{" "}
            {membersMap[request.member]?.lastName || ""}
            <MemberMarkers
                isTestMember={membersMap[request.member]?.isTestMember === true}
                isVipMember={membersMap[request.member]?.isVipMember === true}
            />
        </span>
    );

    const requestLocation = request.locationGoogleFormattedAddress;

    const requestStatus = (
        <div className="request-card__status-title">
            {RequestStatusMap[request.status]}
            {requestOutcome(request) && (
                <>
                    <span>·</span>
                    <span className="request-card__status-title__step">
                        {requestOutcome(request)}
                    </span>
                </>
            )}
        </div>
    );

    const showRequestStatusTag = () => {
        if (!showStatusTag) return false;
        if (request.status === "open") return false;
        if (request.nextDeadlineStep) return false;
        return true;
    };

    const requestStatusTag = (
        <Tag.XSmall.N90 disabled={request.status !== "closed"}>
            {RequestStatusMap[request.status]}
        </Tag.XSmall.N90>
    );

    const requestRating = request.memberFeedbackTimestamp ? (
        <Stars maxRating={5} userRating={request.memberRating ?? 0} />
    ) : null;

    const requestDeadlineStep = (
        <Label.Small.Normal.N50 className="request-card__status-details">
            {request.status === "new" && (
                <TimeTag label="Added" time={fromTimestampToDate(request.createdAt)} />
            )}
            {request.nextDeadlineStep && (
                <Tag.XSmall.N90
                    disabled={isRequestActionable(request) === "non-actionable"}
                >
                    {DeadlineStepToTextMap[request.nextDeadlineStep]}
                </Tag.XSmall.N90>
            )}
            {requestNoFurtherAction(request) && (
                <Tag.XSmall.N90 disabled>No further action</Tag.XSmall.N90>
            )}
            <DeadlineTag deadlineAt={request.nextDeadlineAt} />
        </Label.Small.Normal.N50>
    );

    const agentAvatar = agent ? (
        <AgentAvatarWithSecondary
            primaryAgentId={agent.id}
            secondaryAgentId={
                !isAssignedToMemberOwner ? agentTheMemberIsAssignedTo?.id : undefined
            }
        />
    ) : null;

    const agentAvatarWithNames = agent ? (
        <>
            {agentAvatar}
            <div className="request-card__assignee-list">
                <span className="request-assignee">{agent.name}</span>
                {!isAssignedToMemberOwner && agentTheMemberIsAssignedTo && (
                    <span className="member-assignee">
                        {agentTheMemberIsAssignedTo.name}
                    </span>
                )}
            </div>
        </>
    ) : null;

    const requestDetails = (
        <Label.Small.Normal.N50 className="request-card__request-details">
            {requestDateRange}

            {showMemberName && (
                <>
                    <InlineTextDelimiter />
                    {memberName}
                </>
            )}
            {requestLocation && (
                <>
                    <InlineTextDelimiter />
                    {requestLocation}
                </>
            )}
            <InlineTextDelimiter />
            {requestCategory}
        </Label.Small.Normal.N50>
    );

    const horizontalLayout = (
        <>
            <List.ItemColumn className="flex-[0.45]">
                <div className="request-card__request-meta">
                    {categoryIcon}
                    {ticketNumber}
                </div>

                <List.ItemColumnContainer className="request-card__detail-container">
                    {requestTitle}
                    {requestDetails}
                </List.ItemColumnContainer>
            </List.ItemColumn>

            <List.ItemColumn className="flex-[0.25]">
                <List.ItemColumnContainer>
                    {requestStatus}
                    {requestDeadlineStep}
                    {requestRating}
                </List.ItemColumnContainer>
            </List.ItemColumn>

            <List.ItemColumn className="flex-[0.15] items-center">
                <span className="text-(length:--a-ds-font-size-medium) text-(--a-ds-font-color-secondary)">
                    {membershipTierName || "—"}
                </span>
            </List.ItemColumn>

            <List.ItemColumn className="request-card__assignee flex-[0.15]">
                {agentAvatarWithNames}
                {!agent && currentAgent && (
                    <AssignToMeButton
                        request={request.id}
                        currentAgent={currentAgent}
                    />
                )}
            </List.ItemColumn>
        </>
    );

    const verticalLayout = (
        <>
            <div className="request-card__vertical-row-fill">
                <div className="request-card__vertical-row">
                    {ticketNumber}
                    {showRequestStatusTag() && requestStatusTag}
                    {requestDeadlineStep}
                </div>
                {agentAvatar}
            </div>
            <div className="request-card__vertical-row">{requestTitle}</div>
            {requestRating && (
                <div className="request-card__vertical-row">{requestRating}</div>
            )}
            <div className="request-card__vertical-row-fill">
                <div className="request-card__vertical-row">{requestDetails}</div>
                {showEditButton && mobileEditButton}
            </div>
        </>
    );

    return (
        <NavLink
            id={request.id}
            className={classNames(
                "list-item",
                "request-card",
                `request-card--${layout}`,
                {
                    assigned: !!request.assignedTo,
                    unassigned: !request.assignedTo,
                },
            )}
            to={url}
            activeClassName="selected"
        >
            {layout === "horizontal" ? horizontalLayout : verticalLayout}
        </NavLink>
    );
};

export default RequestCard;
